import { isArray, isArrayEmpty, getItemById } from './helpers';
import { formatDate } from './dateUtils';
import { CARD_DATE_FORMAT } from '../constants/dashboard';
import {
  NOTIFICATION_MODELS,
  NOTIFICATION_MODELS_MAP,
  NOTIFICATION_ACTIONS_MAP,
  NOTIFICATION_MODEL_ICONS,
  NOTIFICATION_ROUTES,
  NOTIFICATION_CREATE_ACTIONS,
  TEAM_NOTIFICATION_UPDATE_FIELDS,
} from '../constants/globalNotifications';
import { PERMISSION_CODES } from '../constants/permissions';

const getNotificationList = notifications => {
  if (isArray(notifications)) return notifications;
  if (isArray(notifications?.results)) return notifications.results;

  return [];
};

const prepareNotificationRoute = (
  model,
  itemId,
  userId,
  canAccessUserProfileCheck,
  canAccessDetailsCheck
) => {
  const { ACTION_PLAN, ONE_ON_ONE, DOCUMENT } = NOTIFICATION_MODELS;
  const { notes, oneOnOne, actionPlan, documents } = NOTIFICATION_ROUTES;
  const canAccessProfile = canAccessUserProfileCheck(userId);

  if (model === ACTION_PLAN) {
    const canAccessPlanDetails = canAccessDetailsCheck(
      userId,
      PERMISSION_CODES.PLANS_VIEW
    );

    return canAccessPlanDetails ? `/${actionPlan}/${itemId}/details` : null;
  }
  if (model === ONE_ON_ONE) {
    const canAccessOneOnOneDetails = canAccessDetailsCheck(
      userId,
      PERMISSION_CODES.ONE_ON_ONE_VIEW
    );
    return canAccessOneOnOneDetails ? `/${oneOnOne}/${itemId}/details` : null;
  }
  if (model === DOCUMENT) {
    return canAccessProfile ? `/people/${userId}/${documents}` : null;
  }
  return canAccessProfile ? `/people/${userId}/${notes}` : null;
};

const prepareNotificationCreatedAction = model => {
  if (model === NOTIFICATION_MODELS.ACTION_PLAN) {
    return [NOTIFICATION_CREATE_ACTIONS.ACTION_PLAN];
  }

  if (model === NOTIFICATION_MODELS.NOTE) {
    return [NOTIFICATION_CREATE_ACTIONS.NOTE];
  }

  if (model === NOTIFICATION_MODELS.ONE_ON_ONE) {
    return [NOTIFICATION_CREATE_ACTIONS.ONE_ON_ONE];
  }

  return [];
};

const getNotificationUser = subject => {
  if (subject && typeof subject === 'object') {
    return {
      user: subject,
      userId: subject.id,
    };
  }

  return {
    user: undefined,
    userId: subject,
  };
};

export const prepareNotifications = (
  notifications,
  canAccessUserProfileCheck,
  canAccessDetailsCheck
) => {
  const notificationList = getNotificationList(notifications);

  if (isArrayEmpty(notificationList)) return [];

  return notificationList.map(notification => {
    const model = getItemById(
      NOTIFICATION_MODELS_MAP,
      notification?.model,
      'value'
    );

    const action = getItemById(
      NOTIFICATION_ACTIONS_MAP,
      notification?.action,
      'value'
    )?.key;

    const { user, userId } = getNotificationUser(notification?.subject);

    const route = prepareNotificationRoute(
      model?.value,
      notification?.object_id,
      userId,
      canAccessUserProfileCheck,
      canAccessDetailsCheck
    );

    const expireDate = notification?.details?.expire_date;

    return {
      ...notification,
      user,
      createdAt: formatDate(notification.created_at),
      expireDate: expireDate
        ? formatDate(expireDate, CARD_DATE_FORMAT)
        : undefined,
      model: model?.key,
      action,
      icon: NOTIFICATION_MODEL_ICONS[model?.key],
      route,
      updates:
        notification?.details?.updated ||
        prepareNotificationCreatedAction(model?.value),
    };
  });
};

export const prepareNotificationItem = (labels, notification) => ({
  model: labels.models[notification?.model],
  action: labels.actions[notification?.action],
  icon: notification.icon,
  createdAt: notification.createdAt,
});

export const prepareNotificationUpdates = (labels, model, updates) => {
  const updateFields = TEAM_NOTIFICATION_UPDATE_FIELDS[model];

  if (!updateFields || !isArray(updates)) return [];

  return Object.keys(updateFields).reduce((acc, field) => {
    const update = updates.find(item => item === field);

    return [...acc, ...(update ? [labels[updateFields[update]]] : [])];
  }, []);
};
